import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useApi } from 'app/shared/contexts/api';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { MindbodyInstanceConfigResponse, MindbodyWidget, MINDBODY_PROVIDER_ADAPTER } from 'app/data/types/mindbody-widget';
import { ErrorNotification } from 'app/shared/types/errors.types';

export const MINDBODY_WIDGETS_QUERY_KEY = 'mindbody-widgets';

const SERVICE_BOOKING_VERTICAL = 'SERVICE_BOOKING';

export const getUseMindbodyWidgetsQueryKey = (buildingUuid: string): QueryKey => [
  MINDBODY_WIDGETS_QUERY_KEY,
  buildingUuid,
];

interface MindbodyWidgetsData {
  widgets: MindbodyWidget[];
  siteId?: number;
  companyUuid?: string;
}

interface UseMindbodyWidgetsQueryResponse {
  widgets: MindbodyWidget[];
  // From the MBO app-instance-config — used to gate registration on mobile.
  siteId?: number;
  companyUuid?: string;
  error: ErrorNotification | null;
  isLoading: boolean;
  isSuccess: boolean;
}

/**
 * Fetches the building's Mindbody widgets (+ site_id/company for the registration gate) via the
 * tenant-accessible vertical endpoint — the admin app-instance-config endpoints require
 * READ_APP_INSTANCE_CONFIG, which tenant users / building guests lack.
 */
export const useMindbodyWidgetsQuery = (): UseMindbodyWidgetsQueryResponse => {
  const { addGenericError, hideGenericErrors } = useErrors();
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const queryKey = getUseMindbodyWidgetsQueryKey(buildingUuid);

  const getWidgetsRequest = async (): Promise<MindbodyWidgetsData> => {
    const headers = await getAuthHeaders();
    const requestHeaders = { ...headers, ...getDefaultHeaders(buildingUuid) };

    // adapterName scopes to Mindbody (vs. e.g. Timekit). A building can have multiple MBO
    // instances, so the endpoint returns all matches and we aggregate widgets across them.
    const response = await apiRequest<MindbodyInstanceConfigResponse[], void, void>({
      url: `${apiUrl}/api/verticals/${SERVICE_BOOKING_VERTICAL}/BUILDING/${buildingUuid}/instance-config?adapterName=${MINDBODY_PROVIDER_ADAPTER}`,
      method: HttpMethod.GET,
      headers: requestHeaders,
    });

    const instances = !response || response.isEmpty() ? [] : response.data ?? [];

    return {
      widgets: instances.flatMap((instance) => instance.config?.widgets ?? []),
      // site_id + company for the registration gate — first instance that has each.
      siteId: instances.find((instance) => instance.config?.site_id != null)?.config?.site_id,
      companyUuid: instances.find((instance) => instance.company?.uuid)?.company?.uuid,
    };
  };

  const { data, error, isLoading, isSuccess } = useQuery<MindbodyWidgetsData, Error>({
    queryKey,
    queryFn: getWidgetsRequest,
    enabled: !!buildingUuid && !!apiUrl,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      addGenericError(MINDBODY_WIDGETS_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(MINDBODY_WIDGETS_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    widgets: data?.widgets ?? [],
    siteId: data?.siteId,
    companyUuid: data?.companyUuid,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
